import React from 'react';

import Browser from '@wetransfer/concorde-browser';
import Cookie from '@wetransfer/concorde-cookie';

import Config from '@app/config';
import Assets from '@app/assets';

import I18n from '@module/i18n';
import LaunchDarkly from '@module/launchdarkly';
import { FF_OUTAGE_BANNER } from '@constant/experiments';
import { STATUS } from '@constant/subscriptions';

const ONEDAY = 24 * 60 * 60 * 1000;

export default {
  sanitizeSubdomain(value) {
    return value
      .toLowerCase()
      .replace(/_|\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  },

  parseSubscriptionDetails(subscription) {
    const { plan, ends_at, order } = subscription;
    const { currency, interval } = plan;
    const { total } = order;

    const currencySymbol = I18n.t('number.currency.' + currency.toUpperCase());
    const autorenewal_attempt = ends_at
      ? I18n.date(new Date(ends_at), 'without_day')
      : null;
    const period = I18n.t('datetime.prompts.' + interval);

    const expiry = new Date(ends_at);
    const daysBetween = this.getDaysBetweenDates(expiry, new Date());

    return {
      deal: currencySymbol + total,
      expiry: I18n.date(expiry, 'without_day'),
      autorenewal_attempt,
      daysBetween,
      period: period.toLowerCase(),
    };
  },

  getSubscriptionKey(subscription) {
    const { cancel_at_period_end, status } = subscription;

    if (status === STATUS.PENDING) return 'page.payment.never_actived_notice'; //Your account is not active yet. Complete your registration to get started with Plus

    if (status === STATUS.ACTIVE && !cancel_at_period_end)
      return 'page.payment.autorenew_notice'; //Your Plus subscription of %{deal} will be renewed automatically on %{autorenewal_attempt}.

    if (status === STATUS.ACTIVE && cancel_at_period_end)
      return 'page.payment.subscription_reactivate_notice'; //Oops - your Plus account has been cancelled and will be disabled in %{daysBetween} days.\nChanged your mind? Don’t panic, it’s easy to get things back up and running.

    if (status === STATUS.PAST_DUE || status === STATUS.CANCELED)
      return 'page.payment.subscription_expired'; //Your Plus account has expired
  },

  generateSubscriptionText(subscription) {
    if (!subscription) {
      return;
    }

    return I18n.t(
      this.getSubscriptionKey(subscription),
      this.parseSubscriptionDetails(subscription)
    );
  },

  getDateWithOffset(date, offset) {
    const newDate = new Date(date.valueOf());
    return new Date(newDate.setMonth(newDate.getMonth() + offset));
  },

  // Highlight text with React element so it's still safe and not XSS-able.
  highlightText(text, filterText, element = 'strong') {
    if (!text) {
      return null;
    }

    const lowercaseText = text.toLowerCase();

    // Only use filter if it matched this text (e.g. either email or label)
    let filter = filterText.toLowerCase();
    if (lowercaseText.indexOf(filter) === -1) {
      filter = '';
    }

    // Get the before, between, after highlightable sections
    const before = text.substring(0, lowercaseText.indexOf(filter));
    const between = text.substring(
      lowercaseText.indexOf(filter),
      lowercaseText.indexOf(filter) + filter.length
    );
    const after = text.substring(lowercaseText.indexOf(filter) + filter.length);

    const HighlightElementTag = element;

    return [
      before || null,
      <HighlightElementTag className="text--is-highlighted" key={text}>
        {between}
      </HighlightElementTag>,
      after || null,
    ];
  },

  getPageIllustration(path) {
    if (Config.route[path] && Config.route[path].pageIllustration) {
      return Config.route[path].pageIllustration;
    }
    return false;
  },

  getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  },

  getAvatar(userId) {
    return Config.avatars[userId % Config.avatars.length];
  },

  getLastUsedSender() {
    let email = Cookie.get('wt_from') || '';
    email = email.split(',');
    return email.length ? email[0].replace(/\s*/g, '') : '';
  },

  getFileExtension(fileName) {
    if (fileName && fileName.indexOf('.') > -1) {
      return fileName.substr(fileName.lastIndexOf('.') + 1).toLowerCase();
    }
    return false;
  },

  getUserFrequency() {
    if (!Cookie.get(Config.cookies.takeover.name)) {
      return 'new';
    }
    return 'exisiting';
  },

  // Probably temporary, during development some of the wt_sent cookie were set to `NaN`, resulting in undesired behaviour.
  getWTSent() {
    return parseInt(
      Cookie.get('wt_sent') === null || isNaN(Cookie.get('wt_sent'))
        ? 0
        : Cookie.get('wt_sent'),
      10
    );
  },

  incrementWTSent() {
    Cookie.set('wt_sent', this.getWTSent() + 1, { days: 365 });
  },

  getWTDownloaded() {
    return parseInt(
      Cookie.get('wt_downloaded') === null || isNaN(Cookie.get('wt_downloaded'))
        ? 0
        : Cookie.get('wt_downloaded'),
      10
    );
  },

  incrementWTDownloaded() {
    Cookie.set('wt_downloaded', this.getWTDownloaded() + 1, { days: 365 });
  },

  validateEmail(email) {
    const re = /\S+@\S+\.\S+/;
    return re.test(email);
  },

  validateUri(uri) {
    const re = /^\S+\.\w{2,}.*$/;
    return re.test(uri);
  },

  scrollToElement(e, target, scrollElement) {
    if (!Browser.isMobile) {
      // Scroll to form
      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
      scrollElement.scrollTo(target, { animated: true, duration: 200 });
    } else {
      target.scrollIntoView();
    }
  },

  getDaysSinceFirstVisit() {
    const firstVisitTimestamp = parseInt(Cookie.get('wt_first_visit'));
    if (firstVisitTimestamp) {
      return Math.floor((Date.now() - firstVisitTimestamp) / ONEDAY);
    }

    return null;
  },

  // TODO: I think it should be possible to do this without the
  // need for this function -- however, for compatibility with
  // existing cookies for now I'll just leave it in here.
  // http://stackoverflow.com/a/11252167
  treatAsUTC(date) {
    if (!date || typeof date['getTimezoneOffset'] !== 'function') {
      date = String(date).split('/');
      if (date.length !== 3) {
        return NaN;
      }
      date = new Date(date.join('/'));
    }

    const result = date;
    result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
    return result;
  },

  getDaysBetweenDates(end, start) {
    return Math.floor(
      Math.abs(this.treatAsUTC(start) - this.treatAsUTC(end)) / ONEDAY
    );
  },

  getFirstVisitDate() {
    const firstVisitTimestamp = parseInt(Cookie.get('wt_first_visit'), 10);
    if (firstVisitTimestamp) {
      return new Date(firstVisitTimestamp).toDateString();
    }
  },

  getFormattedPrice(price, currency) {
    const upperCasedCurrency = currency.toUpperCase();
    // Get the symbol
    const currencyTranslation = I18n.t(`number.currency.${upperCasedCurrency}`);
    // $120USD or €120
    // USD is post-fixed to make it clearer for users that it is US Dollars we charge
    const formattedPrice =
      upperCasedCurrency === 'USD' ? `${price} ${upperCasedCurrency}` : price;
    return `${currencyTranslation}${formattedPrice}`;
  },

  // Result will be the array of bank objects with updated keys suitable for form element
  // example input:
  // [{ name: 'ING', code: 'ing' }]
  // example output:
  // [{ label: 'ING', value: 'ing' }]
  parseCheckoutOptions(options) {
    const keysMap = {
      name: 'label',
      code: 'value',
    };

    const result = options.map((option) => {
      return Object.keys(option).reduce(
        (accumulator, key) => ({
          ...accumulator,
          ...{ [keysMap[key] || key]: option[key] },
        }),
        {}
      );
    });
    return result;
  },

  getOpenInAppUri({ shortUrl, utmContent = '' }) {
    const params = {
      link: shortUrl,
      ...Config.mobileAppBanner.dynamicLinkParams,
      utm_content: utmContent,
    };

    const appUri = new URL(Config.mobileAppBanner.dynamicLinkBaseUrl);
    for (const key in params) {
      appUri.searchParams.set(key, params[key]);
    }

    return new URL(appUri);
  },

  downloadAppUri() {
    if (Browser.isIphone) {
      return Config.mobileAppBanner.downloadUrliOS;
    }

    if (Browser.isAndroid) {
      return Config.mobileAppBanner.downloadUrlAndroid;
    }

    return Config.mobileAppBanner.downloadUrl;
  },

  getFontsForStripe() {
    if (/https:/.test(location.protocol)) {
      return [
        {
          family: 'Fakt Pro Normal',
          src: `url(${Assets.lookup('faktpro/FaktProWeb-Normal.woff')})`,
        },
      ];
    }
  },

  getWallpaperPreviewType(previewType) {
    //On iPhones, the wallpaper preview does not work if it's a video tag
    //But it does work if it's a background image
    //Yes this is weird, yes it sucks, but hey, browsers.
    if (Browser.isIphone || Browser.isTablet) {
      return 'image';
    }
    return previewType;
  },

  getNoticeOptions({ incompatibleVersion }) {
    const outage = LaunchDarkly.getVariant(FF_OUTAGE_BANNER);

    const {
      shouldShowFeatureBanner,
      featureTranslationKey,
      featureName,
    } = Config.featureBanner;

    // For non-positive messages, the notice color will be gray instead of the default blue
    if (outage === 'transfer_issues' || outage === 'general_issues') {
      return {
        text: I18n.t(`notice.${outage}`, {
          link: 'https://wetransfer.statuspage.io',
        }),
        color: 'dark',
      };
    } else if (Browser.isOutdated(Config.supportedBrowsers)) {
      return {
        text: I18n.t('transfer.outdated_browser.text'),
        color: 'dark',
      };
    } else if (incompatibleVersion) {
      return {
        text: I18n.t('force_update'),
        color: 'dark',
      };
    } else if (
      shouldShowFeatureBanner &&
      Cookie.get(Config.cookies.featureBanner.name) !== featureName
    ) {
      return {
        text: I18n.t(featureTranslationKey),
        onClose: () => {
          Cookie.set(Config.cookies.featureBanner.name, featureName, {
            days: 31,
          });
        },
      };
    }

    return { text: null, color: null };
  },

  isDefaultRecipientInUploadLocked({
    channelDefaultRecipient,
    channelProfileReceivingLocked,
    userLoggedIn,
    channelName,
    accountSubDomainName,
  }) {
    const isDefaultRecipientSet = channelDefaultRecipient !== false;
    if (!isDefaultRecipientSet) return false;

    if (!channelProfileReceivingLocked) return false;

    if (!userLoggedIn) return true;

    if (!accountSubDomainName) {
      return channelName !== false;
    }

    return accountSubDomainName !== channelName;
  },
};
